import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CloudUpload, FileText, Upload, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { useCurrentUserProfile } from '@/hooks/useSupabaseUsers';
import { useShipmentPlanUpload } from '@/hooks/useShipmentPlanUpload';

const ACCEPTED = '.html,.htm';
const MAX_SIZE_MB = 25;

function fileNameToReportName(filename: string): string {
  return filename.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateShipmentPlanDialog({ open, onOpenChange }: Props) {
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile();
  const { uploadShipmentPlan, isUploading, progress, stage, error, reset } = useShipmentPlanUpload();

  const [reportName, setReportName] = useState('');
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearFieldError = (key: string) => {
    setFieldErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFileError('');
    clearFieldError('file');
    if (!f) { setFile(null); return; }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      setFileError(`File vượt quá ${MAX_SIZE_MB}MB`);
      setFile(null);
      return;
    }
    const isHtml = /\.html?$/i.test(f.name) || f.type === 'text/html';
    if (!isHtml) {
      setFileError('Chỉ chấp nhận file HTML (.html / .htm)');
      setFile(null);
      return;
    }
    setFile(f);
    if (!reportName.trim()) {
      setReportName(fileNameToReportName(f.name));
      clearFieldError('reportName');
    }
  };

  const handleClose = () => {
    if (isUploading) return;
    setReportName('');
    setReportDate(new Date().toISOString().split('T')[0]);
    setSummary('');
    setFile(null);
    setFileError('');
    setFieldErrors({});
    setSuccess(false);
    reset();
    onOpenChange(false);
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!reportName.trim()) next.reportName = 'Vui lòng nhập tên báo cáo';
    if (!reportDate) next.reportDate = 'Vui lòng chọn ngày báo cáo';
    if (!file) {
      next.file = 'Vui lòng chọn file HTML';
      setFileError('Vui lòng chọn file HTML');
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !file) return;

    const result = await uploadShipmentPlan({
      file,
      metadata: {
        report_name: reportName.trim(),
        report_date: reportDate,
        summary: summary.trim() || undefined,
        auth_user_id: user?.id,
        user_id: user?.id,
        created_by_user_id: profile?.legacy_id ?? undefined,
      },
    });

    if (result?.success) {
      setSuccess(true);
      setTimeout(() => handleClose(), 1500);
    }
  };

  const stageLabel = stage === 'staging'
    ? 'Đang tải file lên...'
    : stage === 'processing'
    ? 'Đang lưu lên Google Drive...'
    : 'Hoàn tất';

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="flex max-h-[85dvh] w-full max-w-lg flex-col gap-0 overflow-hidden p-0 sm:max-w-[32rem]">
        <div className="shrink-0 border-b px-6 pb-4 pt-6">
          <DialogHeader className="space-y-0">
            <DialogTitle className="flex items-center gap-2 pr-8">
              <CloudUpload className="h-5 w-5 shrink-0" />
              Tạo báo cáo Shipment Plan
            </DialogTitle>
          </DialogHeader>
        </div>

        {success ? (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-8">
              <div className="flex flex-col items-center gap-3 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
                <p className="text-lg font-semibold">Tải lên thành công!</p>
              </div>
            </div>
            <DialogFooter className="shrink-0 gap-2 border-t bg-background px-6 py-4">
              <Button type="button" className="w-full sm:w-auto" onClick={handleClose}>Đóng</Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                <div className="space-y-1">
                  <Label>File báo cáo (.html) *</Label>
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (!isUploading && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    className={`cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-colors hover:border-primary/60 hover:bg-accent/30 ${
                      fieldErrors.file ? 'border-destructive/60' : ''
                    }`}
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                  >
                    {file ? (
                      <div className="flex items-center justify-center gap-2 text-sm">
                        <FileText className="h-4 w-4 shrink-0 text-primary" />
                        <span className="max-w-[16rem] truncate font-medium">{file.name}</span>
                        <span className="text-muted-foreground">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-muted-foreground">
                        <Upload className="h-6 w-6" />
                        <span className="text-sm">Bấm để chọn file (tối đa {MAX_SIZE_MB}MB)</span>
                        <span className="text-xs">Chỉ chấp nhận .html / .htm</span>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED}
                    className="hidden"
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />
                  {(fileError || fieldErrors.file) && (
                    <p className="text-sm text-destructive">{fileError || fieldErrors.file}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="report_name">Tên báo cáo *</Label>
                  <Input
                    id="report_name"
                    value={reportName}
                    onChange={e => { setReportName(e.target.value); clearFieldError('reportName'); }}
                    placeholder="Chọn file để tự động điền tên..."
                    aria-invalid={!!fieldErrors.reportName}
                    disabled={isUploading}
                    required
                  />
                  {fieldErrors.reportName && <p className="text-sm text-destructive">{fieldErrors.reportName}</p>}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="report_date">Ngày báo cáo *</Label>
                  <Input
                    id="report_date"
                    type="date"
                    value={reportDate}
                    onChange={e => { setReportDate(e.target.value); clearFieldError('reportDate'); }}
                    aria-invalid={!!fieldErrors.reportDate}
                    disabled={isUploading}
                    required
                  />
                  {fieldErrors.reportDate && <p className="text-sm text-destructive">{fieldErrors.reportDate}</p>}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="summary">Mô tả (tùy chọn)</Label>
                  <Textarea
                    id="summary"
                    value={summary}
                    onChange={e => setSummary(e.target.value)}
                    rows={3}
                    placeholder="Mô tả ngắn..."
                    disabled={isUploading}
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="shrink-0 flex-col gap-2 border-t bg-background px-6 py-4">
              {isUploading && (
                <div className="w-full space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{stageLabel}</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-1.5" />
                </div>
              )}
              {error && !isUploading && (
                <Alert variant="destructive" className="w-full py-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">{error}</AlertDescription>
                </Alert>
              )}
              <div className="flex w-full justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleClose} disabled={isUploading}>
                  Hủy
                </Button>
                <Button type="submit" disabled={isUploading}>
                  {isUploading ? stageLabel : 'Tạo báo cáo'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
